import React from 'react'

import { showError } from '#components/Modals'
import { type ModernScriptDocType } from '#state/models'
import { useMst } from '#state/MstContext'
import { IFolder } from '#state/types'

import { useNavigation } from './useNavigation'

// the create doc modal displays errors inline
const noErrModal: readonly string[] = ['explorer', 'document pane']

export const useResourceCreation = (
  source: 'dashboard' | 'import' | 'explorer' | 'document pane',
) => {
  const [isCreating, setIsCreating] = React.useState(false)
  const mst = useMst()
  const { navigateToRundown, navigateToScript } = useNavigation()

  const trackCreation = (
    folder: IFolder,
    type: ModernScriptDocType | 'rundown',
  ) => {
    const { currentOrg, analytics } = mst
    if (!currentOrg) {
      return
    }

    const accessLevel = folder.isPrivate ? 'private' : 'shared'
    const format =
      type === 'screenplay'
        ? currentOrg.screenplayFormat.id
        : type === 'variety'
          ? currentOrg.studioFormat.id
          : currentOrg.rundownSchemaName

    analytics.trackDocCreated({
      type: 'script',
      from: source,
      accessLevel,
      format,
    })
  }

  const createScript = async ({
    folder,
    formatId,
    name,
    docType,
  }: {
    folder: IFolder
    formatId: string
    docType?: ModernScriptDocType
    name?: string
  }): Promise<void> => {
    if (!mst.currentOrg) {
      return
    }
    const folderId = folder.id
    setIsCreating(true)

    try {
      await mst.doDebug()
      const script = await mst.currentOrg.createScript({
        folderId,
        name,
        formatId,
        docType,
      })

      trackCreation(folder, script.docType)
      navigateToScript(script.id)
    } catch (e) {
      if (noErrModal.includes(source)) throw new Error('we catch upstream')
      showError({ message: `Couldn't create new script` })
    } finally {
      setIsCreating(false)
    }
  }

  const createRundown = async (folder: IFolder, name?: string) => {
    setIsCreating(true)
    try {
      await mst.doDebug()
      const { rundownId } = await folder.createRundown(name)
      trackCreation(folder, 'rundown')
      navigateToRundown(String(rundownId))
    } catch (e) {
      if (noErrModal.includes(source)) throw new Error('we catch upstream')
      showError({
        message: "Couldn't create new rundown",
      })
    } finally {
      setIsCreating(false)
    }
  }

  const importScript = async ({
    file,
    folder,
  }: {
    file: File
    folder: IFolder
  }) => {
    setIsCreating(true)
    try {
      await mst.doDebug()
      const result = await folder.importScriptFromFdx(file)
      if (result) {
        trackCreation(folder, 'screenplay')
        navigateToScript(result.id)
      }
    } catch (e) {
      if (noErrModal.includes(source)) throw new Error('we catch upstream')
      showError("Couldn't import script from fdx file")
    } finally {
      setIsCreating(false)
    }
  }

  return {
    isCreating,
    createScript,
    createRundown,
    importScript,
  }
}
